import { useState, useRef, useCallback } from 'react';
import { Paperclip, X, FileText, Image, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';
import { useLocalizations } from '@/hooks/useLocalizations';

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
const ACCEPTED_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png',
  'image/webp',
];

const ACCEPT_STRING = '.pdf,.docx,.jpg,.jpeg,.png,.webp';

export interface UploadedDocument {
  fileName: string;
  fileType: string;
  fileSize: number;
  storagePath: string;
  extractedText: string;
  extractionMethod: string;
  uploadedAt: string;
}

interface ChatFileUploadProps {
  onDocumentUploaded: (doc: UploadedDocument) => void;
  disabled?: boolean;
  sessionId?: string;
  className?: string;
}

export function ChatFileUpload({ onDocumentUploaded, disabled, sessionId, className }: ChatFileUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { lComponents } = useLocalizations();

  const handleFileSelect = useCallback(async (file: File) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      toast.error(lComponents('tax_oracle.upload.unsupported_type', { defaultValue: 'Unsupported file type. Use PDF, DOCX, or images.' }));
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      toast.error(lComponents('tax_oracle.upload.too_large', { defaultValue: 'File too large. Maximum size is 20MB.' }));
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (sessionId) {
        formData.append('sessionId', sessionId);
      }

      const { data: { session } } = await supabase.auth.getSession();
      
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/tax-oracle-parse-document`, {
        method: 'POST',
        headers: {
          ...(session?.access_token && { 'Authorization': `Bearer ${session.access_token}` }),
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Upload failed');
      }

      const result = await response.json();
      
      if (result.data) {
        onDocumentUploaded(result.data);
        toast.success(lComponents('tax_oracle.upload.success', { defaultValue: '{{fileName}} uploaded and processed', fileName: file.name }));
      } else {
        throw new Error('Unexpected response format');
      }
    } catch (error) {
      logger.error('Document upload failed', error);
      toast.error(error instanceof Error ? error.message : 'Failed to upload document');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [sessionId, onDocumentUploaded, lComponents]);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT_STRING}
        onChange={handleChange}
        className="hidden"
        disabled={disabled || isUploading}
      />
      <Button
        variant="ghost"
        onClick={handleClick}
        disabled={disabled || isUploading}
        className={cn(
          "h-8 w-8 p-0 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 active:bg-muted transition-colors",
          className
        )}
        type="button"
        aria-label={lComponents('tax_oracle.upload.button_label', { defaultValue: 'Upload document' })}
      >
        {isUploading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Paperclip className="h-4 w-4" />
        )}
      </Button>
    </>
  );
}

/** Document chip shown in chat for uploaded files */
interface DocumentChipProps {
  document: UploadedDocument;
  onRemove?: () => void;
  compact?: boolean;
}

export function DocumentChip({ document, onRemove, compact }: DocumentChipProps) {
  const isImage = document.fileType.startsWith('image/');
  const Icon = isImage ? Image : FileText;

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  };

  return (
    <div className={cn(
      "inline-flex items-center gap-1.5 rounded-lg border border-border/50 bg-muted/50 transition-colors",
      compact ? "px-2 py-0.5 text-xs" : "px-3 py-1.5 text-sm"
    )}>
      <Icon className={cn(compact ? "h-3 w-3" : "h-4 w-4", "text-muted-foreground shrink-0")} />
      <span className="truncate max-w-[200px] text-foreground">{document.fileName}</span>
      <span className="text-muted-foreground shrink-0">{formatSize(document.fileSize)}</span>
      {onRemove && (
        <button
          onClick={onRemove}
          className="ml-0.5 text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className={cn(compact ? "h-3 w-3" : "h-3.5 w-3.5")} />
        </button>
      )}
    </div>
  );
}
